import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { useI18n } from '../../i18n/i18n-context';
import { motionAllowed } from '../../lib/motion';
import { PromoPlayer, type PromoPlayerHandle } from '../promo/PromoPlayer';
import { OVERVIEW } from '../promo/promos';

export interface HomeFilmHandle {
  /** Brings the Studio overview into view and plays it from the beginning. */
  readonly watch: () => void;
}

// The ambient light is the film drawn this small, then blurred and spread behind it.
const LIGHT_WIDTH = 64;
const LIGHT_HEIGHT = 36;
// How often the light takes a new frame, and how much of it: each fades in over the last few.
const LIGHT_EVERY_MS = 66;
const LIGHT_BLEND = 0.2;

/** The general Studio overview on a stage lit by the film's colours. */
export function HomeFilm({ ref }: { readonly ref?: Ref<HomeFilmHandle> }) {
  const { t } = useI18n();
  const stage = useRef<HTMLDivElement>(null);
  const player = useRef<PromoPlayerHandle>(null);
  const light = useRef<HTMLCanvasElement>(null);
  const [started, setStarted] = useState(false);

  function watch() {
    stage.current?.scrollIntoView({
      behavior: motionAllowed() ? 'smooth' : 'auto',
      block: 'center',
    });
    player.current?.playFrom(0);
  }

  useImperativeHandle(ref, () => ({ watch }));

  // The ambient light: the poster's colours at first, then the film's, a few frames blended.
  useEffect(() => {
    const video = player.current?.video;
    const canvas = light.current;
    // jsdom can't draw; elsewhere, without motion, the poster's light stays as it is.
    if (!video || !canvas || typeof window.matchMedia === 'undefined') return;
    const context = canvas.getContext('2d');
    if (!context) return;
    const moving = motionAllowed();
    let frame = 0;
    let last = 0;
    const still = new Image();
    still.onload = () => {
      if (!last) context.drawImage(still, 0, 0, LIGHT_WIDTH, LIGHT_HEIGHT);
    };
    still.src = OVERVIEW.poster;
    const draw = (now: number) => {
      if (now - last >= LIGHT_EVERY_MS && video.readyState >= 2) {
        last = now;
        context.globalAlpha = LIGHT_BLEND;
        context.drawImage(video, 0, 0, LIGHT_WIDTH, LIGHT_HEIGHT);
      }
      frame = requestAnimationFrame(draw);
    };
    const play = () => {
      setStarted(true);
      cancelAnimationFrame(frame);
      if (moving) frame = requestAnimationFrame(draw);
    };
    const stop = () => {
      cancelAnimationFrame(frame);
    };
    video.addEventListener('play', play);
    video.addEventListener('pause', stop);
    video.addEventListener('ended', stop);
    return () => {
      stop();
      still.onload = null;
      video.removeEventListener('play', play);
      video.removeEventListener('pause', stop);
      video.removeEventListener('ended', stop);
    };
  }, []);

  return (
    <div
      className="home-film"
      data-started={started || undefined}
      onPlayCapture={() => {
        setStarted(true);
      }}
    >
      <div className="home-film-stage" ref={stage}>
        <canvas
          className="home-film-light"
          ref={light}
          width={LIGHT_WIDTH}
          height={LIGHT_HEIGHT}
          aria-hidden="true"
        />
        <div className="home-film-frame">
          <PromoPlayer
            ref={player}
            src={OVERVIEW.src}
            poster={OVERVIEW.poster}
            captions={OVERVIEW.captions}
            initialDuration={OVERVIEW.duration}
            label={t.promo.label}
            preload="none"
          />
        </div>
      </div>
    </div>
  );
}
